import Web3 from 'web3'
import BigNumber from 'bignumber.js'
import { Buffer } from 'buffer'
import { Interface } from '@ethersproject/abi'
import { getChainFromId, buildWalletAddEthereumChainParams } from './config.mjs'
import { resolveActiveRpcUrl, withRpcFailover, withRpcTimeout, isRpcRateLimitError, RPC_CALL_TIMEOUT_MS } from './rpcFailover.mjs'
import { getChainRpcUrlsForParallelRead } from './config.mjs'
import {
  getProvider,
  resolveTxSender,
  resolveWalletSender,
  resolveTronTxSender,
  resolveTronSigningProvider,
  getCurrentInjectedProvider,
} from './inject.mjs'
import HieSwapAbi from './abis/HieSwap.js'
import ExchangeAbi from './abis/Exchange.js'
import Erc20Abi from './abis/IERC20.js'
import {
  base58de,
  base58en,
  readTronContractView,
  readTronNativeBalance,
  resolveTronWebInstance,
  requireTronWeb,
  isTronWebProvider,
  getTronTransactionReceipt,
  normalizeTronTxResult,
} from './tron'
import {
  tronApproveWithProvider,
  tronSwapWithProvider,
  tronExchangeWithProvider,
} from './tronOps.mjs'
import { isTronWalletConnectProvider } from '../tronWcDetect.mjs'
import { isMetaMaskTronProvider } from './metamaskTron.mjs'
import { db } from '../utils.mjs'
import { toTronSignMessageHex } from '../walletAuth.mjs'
import { normalizeTransactionHash } from './txHash.mjs'
import { waitForTransactionOnChain } from './txBroadcast.mjs'
import { isWalletConnectMode } from '../walletLifecycle.mjs'
import { applyUserGasSpeedWei } from '../gasSpeedPref.mjs'

export { isValidEvmTxHash, normalizeTransactionHash } from './txHash.mjs'
export { fetchTransactionByHash, waitForTransactionOnChain } from './txBroadcast.mjs'
const WITHDRAW = '0x8971A707c958e1EaE4147FF0318117049f50c6d2'

export const bn = (v) => {
  if(Array.isArray(v))
    v = v[0]
	return new BigNumber(v.toString())
}

export const getWeb3 = (chainId, rpcUrl = null) => {
  const chainConf = getChainFromId(chainId)
  const rpc = rpcUrl || resolveActiveRpcUrl(chainId, chainConf)
  const provider = new Web3.providers.HttpProvider(rpc, {
    keepAlive: false,
    timeout: RPC_CALL_TIMEOUT_MS,
  })
  const web3 = new Web3(provider)
  
  const usdt = new web3.eth.Contract(Erc20Abi, chainConf.usdt)
  const contract = new web3.eth.Contract(HieSwapAbi, chainConf.contract)
  const exchangeContract = new web3.eth.Contract(ExchangeAbi, chainConf.exchangeContract)
  return {web3, contract, usdt, exchangeContract, chainConf}
}
export const sha3 = (v) => {
  const web3 = new Web3()
  return web3.utils.sha3(v)
}

export const tronWebApprove = async (amount, spender, provider = null) =>
  tronApproveWithProvider(provider, amount, spender)

export const tronSwap = async (token, toChainId, to, amount, provider = null) =>
  tronSwapWithProvider(provider, token, toChainId, to, amount)

export const tronExchange = async (token, toChainId, to, amount, provider = null) =>
  tronExchangeWithProvider(provider, token, toChainId, to, amount)

const TRON_CHAIN_ID = 95500

async function getTronErcBalance(chainId, account) {
  const chainConf = getChainFromId(chainId)
  return readTronContractView({
    contractHex: chainConf.usdt,
    accountHex: account,
    fn: 'balanceOf',
    types: ['address'],
    values: [account],
    tronWeb: null,
    skipInjected: true,
  })
}

async function getTronAllowance(chainId, account, spender) {
  const chainConf = getChainFromId(chainId)
  return readTronContractView({
    contractHex: chainConf.usdt,
    accountHex: account,
    fn: 'allowance',
    types: ['address', 'address'],
    values: [account, spender],
    tronWeb: null,
    skipInjected: true,
  })
}

export const getErcBalance = async (chainId, account) => {
  if(!chainId || !account) return '0'
  if (Number(chainId) === TRON_CHAIN_ID) {
    return getTronErcBalance(chainId, account)
  }
  return await withRpcFailover(chainId, async (rpc) => {
    const { usdt } = getWeb3(chainId, rpc)
    return await usdt.methods.balanceOf(account).call()
  })
}
export const getAErcBalance = async (chainId, account) => {
  const chainConf = getChainFromId(chainId)
  return await withRpcFailover(chainId, async (rpc) => {
    const { web3 } = getWeb3(chainId, rpc)
    const ausdt = new web3.eth.Contract(Erc20Abi, chainConf.ausdt)
    return await ausdt.methods.balanceOf(account).call()
  })
}

export const getNaiveBalance = async (chainId, account) => {
  if(!chainId || !account) return '0'
  if (Number(chainId) === TRON_CHAIN_ID) {
    const sun = await readTronNativeBalance(account, resolveTronWebInstance())
    return bn(sun)
  }
  return await withRpcFailover(chainId, async (rpc) => {
    const { web3 } = getWeb3(chainId, rpc)
    const amount = await web3.eth.getBalance(account)
    return bn(amount)
  })
}
export const getGasPrice = async (chainId) => {
  if(!chainId) return '0'
  if (Number(chainId) === TRON_CHAIN_ID) return '420'
  return await withRpcFailover(chainId, async (rpc) => {
    const { web3 } = getWeb3(chainId, rpc)
    return await web3.eth.getGasPrice()
  })
}
export const getAllowance = async (chainId, account, spender = null, opts = {}) => {
  if(!chainId || !account) return '0'
  if (Number(chainId) === TRON_CHAIN_ID) {
    const chainConf = getChainFromId(chainId)
    const target = spender || chainConf.contract
    return getTronAllowance(chainId, account, target)
  }
  const web3 = new Web3()
  let owner = String(account || '').trim()
  let approved = spender
  try {
    owner = web3.utils.toChecksumAddress(owner)
    if (approved) approved = web3.utils.toChecksumAddress(String(approved).trim())
  } catch (_e) {
    void _e
  }
  const rpcOpts = opts.timeoutMs ? { timeoutMs: opts.timeoutMs } : {}
  return await withRpcFailover(chainId, async (rpc) => {
    const { usdt, contract } = getWeb3(chainId, rpc)
    if(approved == null){
      approved = contract._address
    }
    return await usdt.methods.allowance(owner, approved).call()
  }, rpcOpts)
}

async function readAllowanceFromProvider(chainId, account, spender, provider, opts = {}) {
  const { isWcTxInflight } = await import('./walletconnect.mjs')
  const useHttpOnly = opts.useHttpOnly === true || isWcTxInflight()
  if (useHttpOnly || !provider?.request || !isWalletConnect(provider)) {
    return getAllowance(chainId, account, spender, opts)
  }
  const { usdt, contract } = getWeb3(chainId)
  const web3 = new Web3()
  let owner = String(account || '').trim()
  let sp = spender
  try {
    owner = web3.utils.toChecksumAddress(owner)
    sp = web3.utils.toChecksumAddress(String(sp ?? contract._address).trim())
  } catch (_e) {
    sp = sp ?? contract._address
  }
  const data = usdt.methods.allowance(owner, sp).encodeABI()
  const timeoutMs = opts.timeoutMs ?? 8000
  try {
    const raw = await withRpcTimeout(
      provider.request({
        method: 'eth_call',
        params: [{ to: usdt._address, data }, 'latest'],
      }),
      timeoutMs,
      `WC allowance(${Number(chainId)})`,
    )
    if (raw == null || raw === '' || raw === '0x') return '0'
    if (typeof raw === 'string' && raw.startsWith('0x')) {
      return bn(raw).toFixed(0, 1)
    }
    return String(raw)
  } catch (e) {
    console.warn('[hieswap] WC eth_call allowance → HTTP RPC fallback', e?.message || e)
    return getAllowance(chainId, account, spender, opts)
  }
}

/** 多 RPC 并行读 allowance（最多 2 节点，非 WC 确认路径备用） */
async function getAllowanceParallel(chainId, account, spender = null, opts = {}) {
  if (!chainId || !account) return '0'
  if (Number(chainId) === TRON_CHAIN_ID) {
    return getAllowance(chainId, account, spender, opts)
  }
  const conf = getChainFromId(chainId)
  const urls = getChainRpcUrlsForParallelRead(conf)
  if (!urls.length) return getAllowance(chainId, account, spender, opts)
  if (opts.preferSingle === true || urls.length === 1) {
    return getAllowance(chainId, account, spender, opts)
  }

  const web3 = new Web3()
  let owner = String(account || '').trim()
  let approved = spender
  try {
    owner = web3.utils.toChecksumAddress(owner)
    if (approved) approved = web3.utils.toChecksumAddress(String(approved).trim())
  } catch (_e) {
    void _e
  }

  const timeoutMs = opts.timeoutMs ?? 4000
  const maxParallel = Math.min(Number(opts.maxParallel) || 2, urls.length)
  const startIdx = Number(opts.startIdx) || 0
  const selected = []
  for (let i = 0; i < maxParallel; i += 1) {
    selected.push(urls[(startIdx + i) % urls.length])
  }

  const tasks = selected.map(async (rpc) => {
    const { usdt, contract } = getWeb3(chainId, rpc)
    const sp = approved ?? contract._address
    return withRpcTimeout(
      usdt.methods.allowance(owner, sp).call(),
      timeoutMs,
      `allowance(${Number(chainId)})`,
    )
  })
  const results = await Promise.allSettled(tasks)
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value != null && r.value !== '') {
      return String(r.value)
    }
  }
  throw new Error('ALLOWANCE_READ_FAILED')
}

function allowancePollBackoffMs(err, hits = 0) {
  if (!isRpcRateLimitError(err)) return 0
  return Math.min(2500 + hits * 1500, 12000)
}

function receiptSucceeded(receipt) {
  if (!receipt) return false
  const status = receipt.status
  return status === true || status === '0x1' || Number(status) === 1
}

/** 查询交易回执（EVM eth_getTransactionReceipt / Tron TronGrid） */
export async function fetchTransactionReceipt(chainId, txHash) {
  const hash = String(txHash || '').trim()
  if (!hash) return null
  if (Number(chainId) === TRON_CHAIN_ID) {
    return getTronTransactionReceipt(hash)
  }
  const normalized = hash.startsWith('0x') ? hash : `0x${hash}`
  return await withRpcFailover(chainId, async (rpc) => {
    const { web3 } = getWeb3(chainId, rpc)
    return await web3.eth.getTransactionReceipt(normalized)
  })
}

function normalizeBytes32Hash(txHash) {
  const raw = String(txHash || '').trim()
  if (!raw) return ''
  return raw.startsWith('0x') ? raw : `0x${raw}`
}

/** WC / 部分钱包 eth_sendTransaction 返回值格式不统一 — 见 txHash.mjs */

/**
 * 轮询 allowance 达标（WC 优先走钱包 eth_call，避免浏览器直连公共 RPC 被 CORS/429 卡住）。
 */
async function pollAllowanceSufficient(chainId, account, spender, requiredWei, opts = {}) {
  const target = bn(requiredWei)
  const timeoutMs = opts.timeoutMs ?? 90000
  let intervalMs = opts.intervalMs ?? 900
  const rpcTimeoutMs = opts.rpcTimeoutMs ?? 5000
  const provider = opts.provider ?? null
  const deadline = Date.now() + timeoutMs
  let last = bn(0)
  let tries = 0
  let rateLimitHits = 0
  const readAllowance = (cid, acc, sp) => getAllowance(cid, acc, sp, { timeoutMs: rpcTimeoutMs })

  while (Date.now() < deadline) {
    tries += 1
    try {
      const raw = await readAllowance(chainId, account, spender)
      last = bn(raw)
      if (last.gte(target)) {
        console.info('[hieswap] approve allowance sufficient', {
          chainId,
          account: String(account || '').slice(0, 10),
          tries,
        })
        return last
      }
      rateLimitHits = 0
    } catch (e) {
      console.warn('[hieswap] approve allowance poll', chainId, e?.message || e)
      const backoff = allowancePollBackoffMs(e, rateLimitHits)
      if (backoff > 0) {
        rateLimitHits += 1
        await new Promise((r) => setTimeout(r, backoff))
        continue
      }
    }
    await new Promise((r) => setTimeout(r, intervalMs))
    if (tries > 8) intervalMs = Math.min(intervalMs + 150, 2500)
  }
  return last
}

/**
 * 授权确认：以 allowance 达标为准（比 receipt 更可靠，尤其 FON + WC）。
 */
async function waitForApproveConfirm(chainId, account, spender, requiredWei, txHash, opts = {}) {
  const target = bn(requiredWei)
  const hash = normalizeTransactionHash(txHash)
  const fast = await pollAllowanceSufficient(chainId, account, spender, target.toFixed(0, 1), {
    timeoutMs: opts.timeoutMs ?? 90000,
    intervalMs: opts.intervalMs ?? 900,
    rpcTimeoutMs: opts.rpcTimeoutMs ?? 5000,
    provider: opts.provider ?? null,
  })
  if (fast.gte(target)) return fast

  if (hash) {
    try {
      const receipt = await fetchTransactionReceipt(chainId, hash)
      if (receipt && !receiptSucceeded(receipt)) {
        const err = new Error('TX_REVERTED')
        err.code = 'TX_REVERTED'
        throw err
      }
      if (receipt) {
        const raw = await getAllowance(chainId, account, spender, { timeoutMs: 5000 })
        const last = bn(raw)
        if (last.gte(target)) return last
      }
    } catch (e) {
      if (e?.code === 'TX_REVERTED') throw e
    }
  }
  return fast
}

/** WC 授权：先等手机签名拿到 hash，再用 receipt 确认（避免 tx 未发出就误进「链上确认」） */
async function sendWcApproveConfirm(chainId, from, toAddress, data, walletProvider, spender, amountWei, gasLimit, gasPrice, opts = {}) {
  const target = bn(amountWei)
  const onPhase = typeof opts.onPhase === 'function' ? opts.onPhase : null

  onPhase?.('wallet')
  const sendResult = await walletConnectSend(from, toAddress, data, walletProvider, chainId, {
    gasLimit,
    gasPriceWei: gasPrice,
    timeoutMs: 120000,
    to: toAddress,
  })
  const sendHash = normalizeTransactionHash(sendResult?.transactionHash)
  if (!sendHash) {
    const err = new Error('WC_INVALID_TX_HASH')
    err.code = 'WC_INVALID_TX_HASH'
    throw err
  }

  onPhase?.('chain')
  console.info('[hieswap] WC approve wallet returned hash', { chainId, hash: sendHash })

  const chainConf = getChainFromId(chainId)
  await waitForTransactionOnChain(chainId, sendHash, {
    timeoutMs: 90000,
    intervalMs: 2500,
    expectedFrom: from,
    expectedTo: chainConf.usdt,
  })
  console.info('[hieswap] WC approve tx visible on chain', { chainId, hash: sendHash })

  /* tx 已上链（pending），等 receipt + allowance 并行确认；FON 等链 RPC 较慢适当放宽超时 */
  const chainConf2 = getChainFromId(chainId)
  const isSlowChain = Number(chainConf2?.gasPriceFloor ?? 0) >= 100  // FON gasPriceFloor=180
  const receiptTimeoutMs = isSlowChain ? 90000 : 45000
  const allowanceTimeoutMs = isSlowChain ? 120000 : 60000

  const receiptPromise = waitForTransactionReceipt(chainId, sendHash, {
    timeoutMs: receiptTimeoutMs,
    intervalMs: 2000,
  }).catch((e) => {
    if (e?.code === 'TX_REVERTED') throw e
    return null
  })

  const allowancePromise = pollAllowanceSufficient(chainId, from, spender, target.toFixed(0, 1), {
    timeoutMs: allowanceTimeoutMs,
    intervalMs: 1500,
    rpcTimeoutMs: 8000,
    provider: null,
  })

  const [receipt, polledAllow] = await Promise.all([receiptPromise, allowancePromise])

  if (receipt && receiptSucceeded(receipt)) {
    let last = polledAllow.gte(target) ? polledAllow : target
    if (!polledAllow.gte(target)) {
      try {
        const raw = await getAllowance(chainId, from, spender, { timeoutMs: 8000 })
        last = bn(raw)
      } catch (_e) {
        void _e
      }
    }
    console.info('[hieswap] WC approve confirmed via receipt', { chainId, hash: sendHash.slice(0, 14) })
    return {
      transactionHash: sendHash,
      __allowanceConfirmed: last.gte(target),
      allowance: last.gte(target) ? last : target,
    }
  }

  if (polledAllow.gte(target)) {
    console.info('[hieswap] WC approve confirmed via allowance poll', { chainId, hash: sendHash.slice(0, 14) })
    return {
      transactionHash: sendHash,
      __allowanceConfirmed: true,
      allowance: polledAllow,
    }
  }

  const err = new Error('TX_RECEIPT_TIMEOUT')
  err.code = 'TX_RECEIPT_TIMEOUT'
  err.transactionHash = sendHash
  throw err
}

/** 跨链进度：查询目标链 hashMap（Tron 走 TronGrid，EVM 走 eth_call） */
export async function queryHashMapOnChain(toChainId, txHash, fromChainId, opts = {}) {
  const toId = Number(toChainId)
  const fromId = Number(fromChainId)
  const hashKey = normalizeBytes32Hash(txHash)
  if (!hashKey || !toId || !fromId) return '0'

  if (toId === TRON_CHAIN_ID) {
    const chainConf = getChainFromId(TRON_CHAIN_ID)
    const contractHex = opts.exchange ? chainConf.exchangeContract : chainConf.contract
    try {
      const res = await readTronContractView({
        contractHex,
        fn: 'hashMap(bytes32,uint256)',
        types: ['bytes32', 'uint256'],
        values: [hashKey, fromId],
        skipInjected: true,
      })
      return String(res ?? '0')
    } catch (_e) {
      return '0'
    }
  }

  return await withRpcFailover(toId, async (rpc) => {
    const { contract: c, exchangeContract: ex } = getWeb3(toId, rpc)
    const t = opts.exchange ? ex : c
    return await t.methods.hashMap(hashKey, fromId).call()
  })
}

/** 轮询交易回执，直到上链成功或超时 */
export async function waitForTransactionReceipt(chainId, txHash, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 120000
  const intervalMs = opts.intervalMs ?? 2000
  const hash = String(txHash || '').trim()
  if (!hash) {
    const err = new Error('MISSING_TX_HASH')
    err.code = 'MISSING_TX_HASH'
    throw err
  }
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    try {
      const receipt = await fetchTransactionReceipt(chainId, hash)
      if (receipt) {
        if (!receiptSucceeded(receipt)) {
          const err = new Error('TX_REVERTED')
          err.code = 'TX_REVERTED'
          throw err
        }
        return receipt
      }
    } catch (e) {
      if (e?.code === 'TX_REVERTED') throw e
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  const err = new Error('TX_RECEIPT_TIMEOUT')
  err.code = 'TX_RECEIPT_TIMEOUT'
  throw err
}

/** 授权上链后轮询 allowance，兼容 RPC 索引延迟 */
export async function waitForAllowanceAtLeast(
  chainId,
  account,
  spender,
  requiredWei,
  opts = {},
) {
  const timeoutMs = opts.timeoutMs ?? 90000
  const intervalMs = opts.intervalMs ?? 2500
  const required = bn(requiredWei)
  const deadline = Date.now() + timeoutMs
  let last = bn(0)
  while (Date.now() < deadline) {
    const raw = await getAllowance(chainId, account, spender)
    last = bn(raw)
    if (last.gte(required)) return last
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  return last
}

/**
 * 发起 approve（如需）→ 等待 receipt → 轮询 allowance 达标。
 * 返回 { allowance, transactionHash }。
 */
export async function approveAndConfirmAllowance(
  chainId,
  account,
  amountWei,
  provider,
  spender = null,
  confirmOpts = {},
) {
  const { contract } = getWeb3(chainId)
  if (spender == null) spender = contract._address
  const target = bn(amountWei)
  if (target.lte(0)) throw 'error arguments'

  let current = bn(await getAllowance(chainId, account, spender))
  if (current.gte(target)) {
    return { allowance: current, transactionHash: null }
  }

  const txRes = await approve(chainId, account, amountWei, provider, spender, confirmOpts)
  if (txRes?.__allowanceConfirmed && bn(txRes.allowance).gte(target)) {
    return { allowance: bn(txRes.allowance), transactionHash: txRes?.transactionHash || null }
  }
  const hash = normalizeTransactionHash(txRes?.transactionHash)

  current = await waitForApproveConfirm(chainId, account, spender, target.toFixed(0, 1), hash, {
    timeoutMs: 90000,
    intervalMs: 600,
    rpcTimeoutMs: 5000,
    provider,
  })
  if (current.lt(target)) {
    const err = new Error('APPROVAL_AMOUNT_INSUFFICIENT')
    err.code = 'APPROVAL_AMOUNT_INSUFFICIENT'
    throw err
  }
  return { allowance: current, transactionHash: hash || txRes?.transactionHash || null }
}
/**
 * 注入钱包切链：await、空 provider 时回退 bitkeep/ethereum、4902 时尝试 addChain。
 * 部分 App 内 WebView（Bitget / AVE 等）不抛错但也不立刻切链，需配合 `waitForChainIdMatch` 轮询。
 */
export async function switchChain(provider, chainId) {
  if (isWalletConnect(provider)) {
    const { ensureWalletConnectOnTargetChain } = await import('./walletconnect.mjs')
    await ensureWalletConnectOnTargetChain(provider, chainId)
    return
  }
  const p =
    provider && typeof provider.request === 'function' ? provider : getProvider()
  if (!p || typeof p.request !== 'function') {
    const err = new Error('NO_EIP1193_PROVIDER')
    err.code = 'NO_PROVIDER'
    throw err
  }
  const hex = '0x' + Number(chainId).toString(16)
  try {
    await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
  } catch (e) {
    const code = e?.code
    const msg = String(e?.message || e || '').toLowerCase()
    const needAdd =
      code === 4902 ||
      code === -32603 ||
      msg.includes('unrecognized chain') ||
      msg.includes('not added') ||
      msg.includes('not configured') ||
      msg.includes('wrong network')
    if (!needAdd) throw e
    const params = buildWalletAddEthereumChainParams(chainId)
    if (!params) throw e
    await p.request({ method: 'wallet_addEthereumChain', params: [params] })
    await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
  }
}

/** 轮询 `eth_chainId` 直到与目标一致或超时（补偿部分钱包不触发 chainChanged / 不整页刷新的情况） */
export async function waitForChainIdMatch(provider, targetChainId, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 12000
  const intervalMs = opts.intervalMs ?? 220
  const p =
    provider && typeof provider.request === 'function' ? provider : getProvider()
  if (!p?.request) return false
  const want = Number(targetChainId)
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    try {
      const raw = await p.request({ method: 'eth_chainId' })
      const cur =
        typeof raw === 'string' && raw.startsWith('0x')
          ? parseInt(raw, 16)
          : Number(raw)
      if (Number(cur) === want) return true
    } catch (_e) {
      void _e
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  return false
}
function isWalletConnect(provider) {
  if (isWalletConnectMode(provider)) return true
  if (provider && typeof provider.request === 'function' && !isWalletConnectMode(provider)) {
    return false
  }
  return db.get('wallet_type') === 'wallet_connect'
}

function toHexQty(value) {
  const web3 = new Web3()
  return web3.utils.toHex(String(value))
}

/** 注入钱包：用 EIP-1193 eth_sendTransaction；from 与钱包当前账户对齐，避免 invalid sender */
async function walletSendTransaction(walletProvider, chainId, { from, to, data, gasLimit, gasPriceWei, value = '0x0' }) {
  const web3 = new Web3()
  let fromAddr = String(from || '').trim()
  let toAddr = String(to || '').trim()
  try {
    if (fromAddr) fromAddr = web3.utils.toChecksumAddress(fromAddr)
    if (toAddr) toAddr = web3.utils.toChecksumAddress(toAddr)
  } catch (_e) {
    void _e
  }
  try {
    const accounts = await walletProvider.request({ method: 'eth_accounts' })
    const active = Array.isArray(accounts) ? accounts[0] : ''
    if (active) {
      const walletFrom = web3.utils.toChecksumAddress(active)
      if (!fromAddr || walletFrom.toLowerCase() !== fromAddr.toLowerCase()) {
        fromAddr = walletFrom
      }
    }
  } catch (_e) {
    void _e
  }
  const tx = {
    from: fromAddr,
    to: toAddr,
    data,
    value,
    gas: toHexQty(gasLimit),
  }
  if (gasPriceWei != null && String(gasPriceWei) !== '' && Number(gasPriceWei) > 0) {
    tx.gasPrice = toHexQty(gasPriceWei)
  }
  if (chainId != null && Number(chainId) !== 95500) {
    tx.chainId = toHexQty(chainId)
  }
  const hash = await walletProvider.request({
    method: 'eth_sendTransaction',
    params: [tx],
  })
  return { transactionHash: hash }
}

function resolveWcTxToAddress(contractOrTo, fallbackTo = '') {
  if (typeof contractOrTo === 'string' && contractOrTo.startsWith('0x')) return contractOrTo.trim()
  const addr =
    contractOrTo?._address ||
    contractOrTo?.options?.address ||
    fallbackTo ||
    ''
  return String(addr || '').trim()
}

async function walletConnectSend(account, contractOrTo, data, provider, chainId, opts = {}) {
  const { ensureWcSessionReadyForTx, wcSafeRequest } = await import('./walletconnect.mjs')
  const { withRpcFailover } = await import('./rpcFailover.mjs')
  const wcProvider = await ensureWcSessionReadyForTx(provider, chainId)

  /* 关键：设置 WC provider 的 chainId，让 SDK 把请求路由到正确 namespace（eip155:chainId）
     不设的话 provider 可能还保留上一次 session 的 chainId（如 BSC），导致 tx 走 BSC namespace */
  const targetChainId = Number(chainId)
  if (wcProvider.chainId !== targetChainId) {
    wcProvider.chainId = targetChainId
    console.info('[WC:evm] provider chainId set to', targetChainId)
  }

  const web3 = new Web3()
  let from = String(account || '').trim()
  let to = resolveWcTxToAddress(contractOrTo, opts.to)
  try {
    from = web3.utils.toChecksumAddress(from)
    if (to) to = web3.utils.toChecksumAddress(to)
  } catch (_e) {
    void _e
  }
  if (!to) {
    const err = new Error('WC_TX_MISSING_TO')
    err.code = 'WC_TX_MISSING_TO'
    throw err
  }

  let nonceHex
  try {
    const nonce = await withRpcFailover(chainId, async (rpc) => {
      const w3 = new Web3(rpc)
      return await w3.eth.getTransactionCount(from, 'pending')
    }, { timeoutMs: 8000 })
    nonceHex = toHexQty(nonce)
  } catch (e) {
    console.warn('[WC:evm] nonce fetch failed, wallet will fill', e?.message || e)
  }

  const tx = {
    from,
    to,
    data,
    value: '0x0',
    chainId: toHexQty(chainId),
  }
  if (nonceHex) tx.nonce = nonceHex
  if (opts.gasLimit) tx.gas = toHexQty(opts.gasLimit)
  if (opts.gasPriceWei && Number(opts.gasPriceWei) > 0) {
    tx.gasPrice = toHexQty(opts.gasPriceWei)
  }

  const WC_SEND_TIMEOUT_MS = opts.timeoutMs ?? 120000
  const topic = String(wcProvider?.session?.topic || wcProvider?.signer?.session?.topic || '')
  console.info('[WC:evm] eth_sendTransaction', {
    chainId: Number(chainId),
    from: from.slice(0, 10),
    to: to.slice(0, 10),
    nonce: nonceHex || '(wallet)',
    topic: topic.slice(0, 12),
    storedTopic: String(db.get('topic', '') || '').slice(0, 12),
  })
  let hash
  try {
    hash = await Promise.race([
      wcSafeRequest(wcProvider, { method: 'eth_sendTransaction', params: [tx] }),
      new Promise((_, reject) => {
        setTimeout(() => {
          const err = new Error('WC_SEND_TX_TIMEOUT')
          err.code = 'WC_SEND_TX_TIMEOUT'
          reject(err)
        }, WC_SEND_TIMEOUT_MS)
      }),
    ])
  } catch (e) {
    const msg = String(e?.message || e?.msg || e || '').toLowerCase()
    console.warn('[WC:evm] eth_sendTransaction failed', e?.code || e?.message || e)
    if (msg.includes('pending session not found') || msg.includes('session not found')) {
      const err = new Error('WC_SESSION_STALE')
      err.code = 'WC_SESSION_STALE'
      throw err
    }
    throw e
  }
  try {
    hash = normalizeTransactionHash(hash)
  } catch (normErr) {
    console.warn('[WC:evm] hash normalize crash', normErr)
    hash = ''
  }
  if (!hash) {
    const err = new Error('WC_INVALID_TX_HASH')
    err.code = 'WC_INVALID_TX_HASH'
    throw err
  }
  console.info('[WC:evm] eth_sendTransaction hash', hash)
  return { transactionHash: hash }
}
async function estimateContractGas(method, rpcMethod, account) {
  try {
    return await method.estimateGas({ from: account })
  } catch (e) {
    if (rpcMethod) return await rpcMethod.estimateGas({ from: account })
    throw e
  }
}

/** 仅 Ethereum / Polygon 官方 USDT 需先 approve(0) 再改额度；FON/BSC 等标准 ERC20 不需要 */
function needsTetherStyleAllowanceReset(usdtAddress) {
  const addr = String(usdtAddress || '').toLowerCase()
  return (
    addr === '0xdac17f958d2ee523a2206206994597c13d831ec7' ||
    addr === '0xc2132d05d31c914a87c6611c10748aeb04b58e8f'
  )
}

async function assertNativeGasForTx(chainId, account, gasLimit, gasPriceWei) {
  if (!account || chainId == 95500) return
  const chainConf = getChainFromId(chainId)
  const bal = await getNaiveBalance(chainId, account)
  const need = bn(gasLimit).times(bn(gasPriceWei)).times(1.06)
  if (bn(bal).lt(need)) {
    const err = new Error('INSUFFICIENT_NATIVE_FOR_GAS')
    err.code = 'INSUFFICIENT_NATIVE_FOR_GAS'
    err.coin = chainConf?.coin || 'ETH'
    throw err
  }
}

async function prepareWalletSend(provider, account, chainId, contracts = []) {
  const wcMode = isWalletConnect(provider)
  const { provider: walletProvider, account: from } = await resolveTxSender(provider, account)
  const want = Number(chainId)
  if (wcMode) {
    /* 切链已在 prepareEvmTxProvider / ensureWcSessionReadyForTx 完成，此处不再重复 request */
    for (const c of contracts) {
      if (c?.setProvider) c.setProvider(walletProvider)
    }
    return { provider: walletProvider, account: from }
  }
  await switchChain(walletProvider, want)
  const matched = await waitForChainIdMatch(walletProvider, want, {
    timeoutMs: 14000,
    intervalMs: 220,
  })
  if (!matched) {
    const err = new Error('CHAIN_SWITCH_TIMEOUT')
    err.code = 'CHAIN_SWITCH_TIMEOUT'
    throw err
  }
  for (const c of contracts) {
    if (c?.setProvider) c.setProvider(walletProvider)
  }
  return { provider: walletProvider, account: from }
}

async function sendApproveOnce(chainId, account, amountWei, provider, spender, opts = {}) {
  if (chainId == 95500) return await tronWebApprove(amountWei, spender, provider)
  const chainConf = getChainFromId(chainId)
  const { provider: walletProvider, account: from } = await prepareWalletSend(
    provider,
    account,
    chainId,
    [],
  )
  const gasLimit = Math.ceil(
    Number(
      await withRpcFailover(chainId, async (rpc) => {
        const { web3 } = getWeb3(chainId, rpc)
        const rpcUsdt = new web3.eth.Contract(Erc20Abi, chainConf.usdt)
        return await rpcUsdt.methods.approve(spender, amountWei).estimateGas({ from })
      })
    ) * 1.5
  )
  const gasPrice = await getGasPrice4Send(chainId)
  await assertNativeGasForTx(chainId, from, gasLimit, gasPrice)
  const web3 = new Web3(walletProvider)
  const usdt = new web3.eth.Contract(Erc20Abi, chainConf.usdt)
  if (isWalletConnect(walletProvider)) {
    const data = usdt.methods.approve(spender, amountWei).encodeABI()
    return await sendWcApproveConfirm(
      chainId,
      from,
      chainConf.usdt,
      data,
      walletProvider,
      spender,
      amountWei,
      gasLimit,
      gasPrice,
      { onPhase: opts.onApprovePhase },
    )
  }
  return await sendWithGasRetry(
    usdt.methods.approve(spender, amountWei),
    { from, gasLimit, gasPrice },
    chainId,
    walletProvider,
  )
}

/** 发 EVM 交易前解析 provider：WC 走 session 单例 + 软切链，注入钱包走 ensureProviderOnChain */
export async function prepareEvmTxProvider(provider, chainId) {
  if (Number(chainId) === TRON_CHAIN_ID) {
    return ensureProviderOnChain(provider, chainId)
  }
  if (isWalletConnect(provider) || db.get('wallet_type') === 'wallet_connect') {
    const { resolveEvmWalletConnectProvider, syncWcSessionTopic } = await import('./walletconnect.mjs')
    const wc = resolveEvmWalletConnectProvider(provider)
    syncWcSessionTopic(wc)
    /* Uniswap：连接阶段不切链，发 tx 前 ensureWcSessionReadyForTx 再 switch 一次 */
    return wc
  }
  let eth = provider && typeof provider.request === 'function' ? provider : null
  if (!eth) eth = await getCurrentInjectedProvider()
  if (!eth) eth = getProvider()
  return ensureProviderOnChain(eth, chainId)
}

/** 注入钱包：发起链上操作前切到目标链并等待 eth_chainId 一致 */
export async function ensureProviderOnChain(provider, chainId, opts = {}) {
  if (Number(chainId) === TRON_CHAIN_ID) {
    return await resolveTronSigningProvider(provider)
  }
  if (isWalletConnect(provider)) {
    const {
      ensureWalletConnectOnTargetChain,
      resolveEvmWalletConnectProvider,
    } = await import('./walletconnect.mjs')
    const wcProvider = resolveEvmWalletConnectProvider(provider)
    await ensureWalletConnectOnTargetChain(wcProvider, chainId)
    const matched = await waitForChainIdMatch(wcProvider, chainId, {
      timeoutMs: opts.timeoutMs ?? 16000,
      intervalMs: opts.intervalMs ?? 250,
    })
    if (!matched) {
      console.warn('[hieswap] WC chain switch: eth_chainId not synced yet, proceeding with target', chainId)
      wcProvider.chainId = Number(chainId)
    }
    return wcProvider
  }
  let p =
    provider && typeof provider.request === 'function' ? provider : null
  if (!p) p = await getCurrentInjectedProvider()
  if (!p) p = getProvider()
  if (!p?.request) {
    const err = new Error('NO_EIP1193_PROVIDER')
    err.code = 'NO_PROVIDER'
    throw err
  }
  await switchChain(p, chainId)
  const matched = await waitForChainIdMatch(p, chainId, {
    timeoutMs: opts.timeoutMs ?? 14000,
    intervalMs: opts.intervalMs ?? 200,
  })
  if (!matched) {
    const err = new Error('CHAIN_SWITCH_TIMEOUT')
    err.code = 'CHAIN_SWITCH_TIMEOUT'
    throw err
  }
  return p
}

export const approve = async (chainId, account, amount, provider, spender = null, confirmOpts = {}) => {
  if (!chainId || !account || amount == null || amount === '') throw 'error arguments'
  const { usdt, contract } = getWeb3(chainId)
  if (spender == null) spender = contract._address
  const target = bn(amount)
  if (target.lte(0)) throw 'error arguments'

  if (Number(chainId) === TRON_CHAIN_ID) {
    const current = bn(await getAllowance(chainId, account, spender))
    if (current.gte(target)) return null
    return await tronWebApprove(target.toFixed(0, 1), spender, provider)
  }

  const { account: walletAccount } = await resolveTxSender(provider, account)
  account = walletAccount
  const current = bn(await getAllowance(chainId, account, spender))
  if (current.gte(target)) return null

  const targetWei = target.toFixed(0, 1)
  const mustReset =
    needsTetherStyleAllowanceReset(usdt._address) && current.gt(0) && !current.eq(target)

  if (mustReset) {
    const gasPrice = await getGasPrice4Send(chainId)
    const gl0 = Math.ceil(
      Number(
        await withRpcFailover(chainId, async (rpc) => {
          const { web3, chainConf } = getWeb3(chainId, rpc)
          const rpcUsdt = new web3.eth.Contract(Erc20Abi, chainConf.usdt)
          return await rpcUsdt.methods.approve(spender, '0').estimateGas({ from: account })
        })
      ) * 1.5
    )
    const gl1 = Math.ceil(
      Number(
        await withRpcFailover(chainId, async (rpc) => {
          const { web3, chainConf } = getWeb3(chainId, rpc)
          const rpcUsdt = new web3.eth.Contract(Erc20Abi, chainConf.usdt)
          return await rpcUsdt.methods.approve(spender, targetWei).estimateGas({ from: account })
        })
      ) * 1.5
    )
    await assertNativeGasForTx(chainId, account, gl0 + gl1, gasPrice)
    await sendApproveOnce(chainId, account, '0', provider, spender, confirmOpts)
    return await sendApproveOnce(chainId, account, targetWei, provider, spender, confirmOpts)
  }
  return await sendApproveOnce(chainId, account, targetWei, provider, spender, confirmOpts)
}

export const swap = async (chainId, toChainId, account, to, amount, provider) => {
  if (!chainId || !account || !amount) throw 'error arguments'
  const { usdt, contract } = getWeb3(chainId)
  if (chainId == 95500) return await tronSwap(usdt._address, toChainId, to, amount, provider)
  const methodName = chainId == 56 ? 'crossChain1' : 'crossChain'
  const gasLimit = await withRpcFailover(chainId, async (rpc) => {
    const { web3, chainConf } = getWeb3(chainId, rpc)
    const rpcCall = new web3.eth.Contract(HieSwapAbi, chainConf.contract).methods[methodName](
      chainConf.usdt,
      toChainId,
      to,
      amount,
    )
    return await rpcCall.estimateGas({ from: account })
  })
  const crossCall = contract.methods[methodName](usdt._address, toChainId, to, amount)
  const gasLimitFinal = Math.ceil(gasLimit * 1.5)
  const gasPrice = await getGasPrice4Send(chainId)
  if (isWalletConnect(provider)) {
    return await walletConnectSend(
      account,
      contract,
      crossCall.encodeABI(),
      provider,
      chainId,
      { gasLimit: gasLimitFinal, gasPriceWei: gasPrice },
    )
  }
  const { provider: walletProvider, account: from } = await prepareWalletSend(
    provider,
    account,
    chainId,
    [contract],
  )
  await assertNativeGasForTx(chainId, from, gasLimitFinal, gasPrice)
  return await sendWithGasRetry(
    contract.methods[methodName](usdt._address, toChainId, to, amount),
    { from, gasLimit: gasLimitFinal, gasPrice },
    chainId,
    walletProvider,
  )
}

function isGasPriceTooLowError(err){
  const m = String(err?.message ?? err ?? '').toLowerCase()
  return (
    m.includes('underpriced') ||
    m.includes('fee too low') ||
    m.includes('replacement transaction underpriced') ||
    m.includes('transaction gas price is too low') ||
    m.includes('max fee per gas less than block base fee')
  )
}

function getRetryGasPrice(chainId, currentGasPriceWei){
  const chainConf = getChainFromId(chainId)
  let next = bn(currentGasPriceWei).times(1.35)
  const retryFloorGwei = Number(chainConf.gasPriceRetryFloor ?? (chainId == 56 ? 0.30 : 0))
  if(Number.isFinite(retryFloorGwei) && retryFloorGwei > 0){
    const floorWei = bn(retryFloorGwei).times(1e9)
    if(next.lt(floorWei)) next = floorWei
  }
  return next.integerValue(BigNumber.ROUND_CEIL).toFixed(0)
}

async function sendWithGasRetry(method, txOpts, chainId, walletProvider){
  const from = txOpts?.from
  if (from && txOpts?.gasLimit && txOpts?.gasPrice && chainId != 95500) {
    await assertNativeGasForTx(chainId, from, txOpts.gasLimit, txOpts.gasPrice)
  }
  const contractTo =
    method?._parent?.options?.address ||
    method?._parent?._address
  const data = typeof method?.encodeABI === 'function' ? method.encodeABI() : null
  /* 注入钱包走 web3 contract.send（旧版路径，不手动拼 chainId）；WC 才用手动 eth_sendTransaction */
  const useManualWalletTx =
    walletProvider?.request &&
    (isWalletConnectMode(walletProvider) || db.get('wallet_type') === 'wallet_connect')

  const sendViaWallet = async (gasPrice) => {
    if (!useManualWalletTx || !contractTo || !data) {
      return await method.send({ ...txOpts, gasPrice })
    }
    return await walletSendTransaction(walletProvider, chainId, {
      from: txOpts.from,
      to: contractTo,
      data,
      gasLimit: txOpts.gasLimit,
      gasPriceWei: gasPrice,
    })
  }

  try{
    return await sendViaWallet(txOpts.gasPrice)
  }catch(e){
    if(!isGasPriceTooLowError(e)) throw e
    const retryGasPrice = getRetryGasPrice(chainId, txOpts.gasPrice)
    if(bn(retryGasPrice).lte(bn(txOpts.gasPrice))) throw e
    return await sendViaWallet(retryGasPrice)
  }
}


export const exchange = async (chainId, toChainId, account, to, amount, provider) => {
  if(!chainId || !account || !amount) throw 'error arguments'
  const {usdt, exchangeContract} = getWeb3(chainId)
  if(chainId == 95500) return await tronExchange(usdt._address, toChainId, to, amount, provider)
  const gasLimit = await withRpcFailover(chainId, async (rpc) => {
    const { web3, chainConf } = getWeb3(chainId, rpc)
    const rpcExchange = new web3.eth.Contract(ExchangeAbi, chainConf.exchangeContract)
    return await rpcExchange.methods.crossChain(chainConf.usdt, toChainId, to, amount).estimateGas({from: account})
  })
  const crossMethod = exchangeContract.methods.crossChain(usdt._address, toChainId, to, amount)
  const gasLimitFinal = Math.ceil(gasLimit * 1.5)
  const gasPrice = await getGasPrice4Send(chainId)
  if (isWalletConnect(provider)) {
    return await walletConnectSend(
      account, 
      exchangeContract, 
      crossMethod.encodeABI(),
      provider,
      chainId,
      { gasLimit: gasLimitFinal, gasPriceWei: gasPrice },
    )
  }
  const { provider: walletProvider, account: from } = await prepareWalletSend(
    provider,
    account,
    chainId,
    [exchangeContract],
  )
  await assertNativeGasForTx(chainId, from, gasLimitFinal, gasPrice)
  return await sendWithGasRetry(
    exchangeContract.methods.crossChain(usdt._address, toChainId, to, amount),
    {from, gasLimit: gasLimitFinal, gasPrice},
    chainId,
    walletProvider,
  )
}

async function getGasPrice4Send(chainId){
  const chainConf = getChainFromId(chainId)
  const applySendGasPolicy = (rawGasWei) => {
    let gasWei = bn(rawGasWei)
    const bump = Number(chainConf.gasPriceBump ?? 1.08)
    if(Number.isFinite(bump) && bump > 1){
      gasWei = gasWei.times(bump)
    }
    const floorGwei = Number(chainConf.gasPriceFloor ?? 0)
    if(Number.isFinite(floorGwei) && floorGwei > 0){
      const floorWei = bn(floorGwei).times(1e9)
      if(gasWei.lt(floorWei)){
        gasWei = floorWei
      }
    }
    return gasWei.integerValue(BigNumber.ROUND_CEIL).toFixed(0)
  }
  let computed
  // User-side tx cost should follow live network conditions first.
  try{
    const liveGasPrice = await getGasPrice(chainId)
    if(liveGasPrice && Number(liveGasPrice) > 0){
      computed = applySendGasPolicy(liveGasPrice)
    }
  }catch(_e){
    void _e
  }
  if(!computed){
    // Fallback: use configured static gas price only when live RPC read fails.
    if(chainConf.gasPrice && chainConf.gasPrice !== 'floating'){
      computed = applySendGasPolicy(Number(chainConf.gasPrice) * 1e9)
    } else {
      computed = applySendGasPolicy(await getGasPrice(chainId))
    }
  }
  return applyUserGasSpeedWei(computed, chainConf)
}

export const getGasFee = (chainConf, gasPrice) => {
  if(chainConf.gasFee != 'floating') return chainConf.gasFee
  const gWei = bn(gasPrice != null && gasPrice !== '' ? gasPrice : 0)
  if (!gWei.isFinite() || gWei.lte(0)) return 'loading'
  return gWei
    .times(130000)
    .div(bn(10).pow(18))
    .times(bn(chainConf.nativePrice))
    .times(bn(1.1))
    .toFixed(2)
}

const FSC_CHAIN_ID = 201022

function normalizeBridgeGasFloor(res) {
  if (res === 'loading' || res === '' || res == null) return res
  const n = Number(res)
  if (Number.isFinite(n) && n > 0 && n < 0.15) return '0.15'
  return res
}

/** 跨链展示用 GAS（USDT）：始终按目标链；入账 FSC 限时免费 */
export function getBridgeGasFeeForDisplay(fromChain, toChain, fromGasPrice, toGasPrice) {
  const toId = Number(toChain?.chainId)
  if (toId === FSC_CHAIN_ID) return 0
  return normalizeBridgeGasFloor(getGasFee(toChain, toGasPrice))
}

/** 跨链到账扣减用 GAS（USDT）：始终按目标链配置 */
export function getBridgeGasFeeForDeduct(toChain, toGasPrice) {
  return normalizeBridgeGasFloor(getGasFee(toChain, toGasPrice))
}

export const signMessage = async (provider, msg, account) => {
  if (isTronWalletConnectProvider(provider)) {
    return await provider._tronWallet.signMessage(toTronSignMessageHex(msg))
  }
  if (isMetaMaskTronProvider(provider)) {
    // @metamask/connect-tron 内部 base64(utf8)，勿传 Tron hex
    return await provider._metaMaskTronAdapter.signMessage(String(msg ?? ''))
  }
  if (isWalletConnect(provider)) {
    return await provider.request({ method: 'personal_sign', params: [msg, account] })
  }
  // EVM 注入钱包：必须用传入的 EIP-1193 provider，禁止回落全局 tronWeb（TP 等同装会劫持）
  if (provider && typeof provider.request === 'function' && !isTronWebProvider(provider)) {
    return await provider.request({ method: 'personal_sign', params: [msg, account] })
  }
  if (isTronWebProvider(provider) && provider?.trx?.signMessageV2) {
    return await provider.trx.signMessageV2(toTronSignMessageHex(msg))
  }
  const tw = resolveTronWebInstance()
  if (tw?.trx?.signMessageV2) {
    return await tw.trx.signMessageV2(toTronSignMessageHex(msg))
  }
  if (provider?.request) {
    return await provider.request({ method: 'personal_sign', params: [msg, account] })
  }
  throw new Error('NO_SIGN_PROVIDER')
}

export const withdraw = async (provider, account, amount, deadline, r, s, v) => {
  const web3 = new Web3(resolveActiveRpcUrl(201022))
  const abi = [{ "inputs": [ { "internalType": "uint256", "name": "amount", "type": "uint256" }, { "internalType": "uint256", "name": "deadline", "type": "uint256" }, { "internalType": "bytes32", "name": "r", "type": "bytes32" }, { "internalType": "bytes32", "name": "s", "type": "bytes32" }, { "internalType": "uint8", "name": "v", "type": "uint8" } ], "name": "withdraw", "outputs": [], "stateMutability": "nonpayable", "type": "function" }]
  const contract = new web3.eth.Contract(abi, WITHDRAW)
  if (isWalletConnect(provider)) {
    return await walletConnectSend(
      account, 
      contract, 
      contract.methods.withdraw(amount, deadline, r.toLowerCase(), s.toLowerCase(), v.toLowerCase()).encodeABI(),
      provider,
      201022,
    )
  }else{
    contract.setProvider(provider)
    const gasPrice = await getGasPrice4Send(201022)
    return await contract.methods.withdraw(amount, deadline, r.toLowerCase(), s.toLowerCase(), v.toLowerCase()).send({from: account, gasLimit: 1000000, gasPrice})
  }
}